// Utilities for mapping menu item links to custom page slugs and back.
//
// The home page ("/") is served by the Login custom page for visitors who
// are not signed in, so "/" and "/login" both resolve to the login slug.

const LOGIN_SLUG = "login";
const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;
const SYSTEM_PAGE_PATHS = {
  login: "/",
  signup: "/signup",
  join: "/join",
  privacy: "/site/privacy",
  terms: "/site/terms",
};

function splitLink(link) {
  const value = `${link || ""}`.trim();
  if (!value) {
    return { base: "", suffix: "" };
  }

  if (URL_SCHEME_PATTERN.test(value) || value.startsWith("//")) {
    return { base: value, suffix: "" };
  }

  const hashIndex = value.indexOf("#");
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const beforeHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const queryIndex = beforeHash.indexOf("?");
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : "";
  const pathOnly = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const trimmedPath = pathOnly.replace(/\/+$/, "") || "/";

  return {
    base: trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`,
    suffix: `${query}${hash}`,
  };
}

function normalizeLink(link) {
  return splitLink(link).base;
}

function normalizeSystemPagePath(path) {
  if (!path) return "";
  if (path === "/privacy") return SYSTEM_PAGE_PATHS.privacy;
  if (path === "/terms") return SYSTEM_PAGE_PATHS.terms;
  return path;
}

export function isExternalMenuLink(link) {
  const normalized = normalizeLink(link);
  return Boolean(
    normalized &&
      (URL_SCHEME_PATTERN.test(normalized) || normalized.startsWith("//"))
  );
}

export function resolveMenuRuntimeLink(link) {
  const { base, suffix } = splitLink(link);
  if (!base) return "";
  if (isExternalMenuLink(base)) return base;
  return `${normalizeSystemPagePath(base)}${suffix}`;
}

/**
 * Given a menu item `link` and the list of custom pages, return the page
 * object that the link targets, or null if the link is external / unknown.
 */
export function resolveMenuLinkToPage(link, pages) {
  if (!Array.isArray(pages) || pages.length === 0) return null;

  const normalized = normalizeLink(link);
  if (!normalized || isExternalMenuLink(normalized)) return null;

  const runtimeLink = normalizeSystemPagePath(normalized);

  if (runtimeLink === "/" || runtimeLink === "/login") {
    return pages.find((page) => page.slug === LOGIN_SLUG) || null;
  }

  const siteMatch = runtimeLink.match(/^\/site\/([^/?#]+)$/);
  if (siteMatch?.[1]) {
    return pages.find((page) => page.slug === siteMatch[1]) || null;
  }

  const slugMatch = runtimeLink.replace(/^\//, "");
  return pages.find((page) => page.slug === slugMatch) || null;
}

/**
 * Given a page slug, return the canonical link used in menus for that page.
 */
export function getDefaultLinkForPage(slug) {
  return SYSTEM_PAGE_PATHS[slug] || `/site/${slug}`;
}

/**
 * For a given page slug, collect the menus/items that link to it.
 * Returns an array of { menu, item } entries.
 */
export function findMenusLinkingToPage(pageSlug, menus) {
  if (!Array.isArray(menus)) return [];

  const results = [];
  menus.forEach((menu) => {
    if (!Array.isArray(menu?.items)) return;

    menu.items.forEach((item) => {
      const linkedPage = resolveMenuLinkToPage(item?.link, [
        { slug: pageSlug },
      ]);
      if (linkedPage?.slug === pageSlug) {
        results.push({ menu, item });
      }
    });
  });

  return results;
}
